import { useState } from 'react';
import { CheckCircle2, AlertTriangle, Clock, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';

const STATUS_INFO = {
  valido: { icon: CheckCircle2, cls: 'border-emerald-400/25 bg-emerald-400/10 text-emerald-200', label: 'CNPJ validado' },
  invalido: { icon: AlertTriangle, cls: 'border-[rgba(255,59,92,0.3)] bg-[rgba(255,59,92,0.1)] text-[rgb(255,150,165)]', label: 'CNPJ inválido' },
  pendente: { icon: Clock, cls: 'border-amber-300/25 bg-amber-300/10 text-amber-200', label: 'CNPJ pendente' },
};

async function callValidate() {
  const { data, error } = await supabase.functions.invoke('validate-cnpj', { body: {} });
  if (error) {
    let msg = 'Não foi possível validar o CNPJ.';
    try {
      const j = await error.context.json();
      if (j?.error) msg = j.error;
    } catch (e) {
      // sem corpo de erro
    }
    throw new Error(msg);
  }
  return data;
}

export function CnpjCheck({ company }) {
  const [busy, setBusy] = useState(false);
  const status = company?.cnpjStatus || 'pendente';
  const info = STATUS_INFO[status] || STATUS_INFO.pendente;
  const Icon = info.icon;
  const hasCnpj = !!company?.cnpj;

  const validate = async () => {
    setBusy(true);
    try {
      const res = await callValidate();
      if (res?.status === 'valido') toast.success(res.message);
      else toast.error(res?.message || 'CNPJ não validado.');
      // Recarrega para o painel e o checklist refletirem o novo status
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="surface mt-4 flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="cnpj-check">
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-0.5 h-5 w-5 text-primary" />
        <div>
          <div className="text-sm font-medium">Validação do CNPJ</div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Confere os dígitos e a situação cadastral na Receita Federal. Ao alterar o CNPJ, é preciso validar de novo.
          </p>
        </div>
      </div>
      <div className="flex flex-col items-start gap-2 sm:items-end">
        <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium ${info.cls}`} data-testid="cnpj-status">
          <Icon className="h-3.5 w-3.5" /> {info.label}
        </span>
        {status !== 'valido' && (
          <>
            <Button size="sm" className="btn-glow" onClick={validate} disabled={busy || !hasCnpj} data-testid="cnpj-validate-button">
              {busy ? <Loader2 className="animate-spin" /> : <ShieldCheck />} Validar CNPJ
            </Button>
            {!hasCnpj && <p className="text-[11px] text-muted-foreground">Informe o CNPJ e salve o perfil antes de validar.</p>}
          </>
        )}
      </div>
    </section>
  );
}